import { Close as CloseIcon } from "@mui/icons-material";
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Select from "@mui/material/Select";

import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { updateCustomerBulk } from "../../../../../api/customers";

export default function AccountBulkEditDialog({
  open,
  onClose,
  selectedAccounts = [],
  onSubmit,
  onShowSuccess
}) {
  const [formData, setFormData] = useState({
    accountType: "",
    manager: "",
    bookkeeper: "",
  });

  // State for bookkeeper search
  const [bookkeepers, setBookkeepers] = useState([]);
  const [selectedBookkeeper, setSelectedBookkeeper] = useState(null);
  const [loadingBookkeepers, setLoadingBookkeepers] = useState(false);

  // State for manager search
  const [managers, setManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState(null);
  const [loadingManagers, setLoadingManagers] = useState(false);

  // State for controlling autocomplete open/close
  const [managerOpen, setManagerOpen] = useState(false);
  const [bookkeeperOpen, setBookkeeperOpen] = useState(false);

  // State for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);

  const searchBookkeepers = useCallback(async (searchTerm = "") => {
    setLoadingBookkeepers(true);
    try {
      const params = {};
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await axios.get("/api/v2/profiles/", {
        headers: { "Content-Type": "application/json" },
        params,
        withCredentials: true,
      });

      const bookkeeperProfiles = (response.data.results || []).filter(
        (profile) => profile.type === 'accountant' || profile.type === 'bookkeeper'
      );

      const bookkeeperOptions = bookkeeperProfiles.map((profile) => {
        const firstName = profile.user.first_name || '';
        const lastName = profile.user.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        const displayName = fullName || profile.user.email;

        return {
          id: profile.id,
          name: displayName,
          email: profile.user.email,
          label: displayName,
          value: profile.user.email,
          firstName,
          lastName,
          type: profile.type,
        };
      });

      setBookkeepers(bookkeeperOptions);
    } catch (error) {
      setBookkeepers([]);
    } finally {
      setLoadingBookkeepers(false);
    }
  }, []);

  const searchManagers = useCallback(async (searchTerm = "") => {
    setLoadingManagers(true);
    try {
      const params = {};
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await axios.get("/api/v2/profiles/", {
        headers: { "Content-Type": "application/json" },
        params,
        withCredentials: true,
      });

      const managerProfiles = (response.data.results || []).filter(
        (profile) => profile.type === 'manager'
      );

      const managerOptions = managerProfiles.map((profile) => {
        const firstName = profile.user.first_name || '';
        const lastName = profile.user.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        const displayName = fullName || profile.user.email;

        return {
          id: profile.id,
          name: displayName,
          email: profile.user.email,
          label: displayName,
          value: profile.user.email,
          firstName,
          lastName,
          type: profile.type,
        };
      });

      setManagers(managerOptions);
    } catch (error) {
      setManagers([]);
    } finally {
      setLoadingManagers(false);
    }
  }, []);

  // Prefetch initial manager and bookkeeper lists
  useEffect(() => {
    if (open) {
      searchManagers();
      searchBookkeepers();
    }
  }, [open, searchManagers, searchBookkeepers]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);

    try {
      // Build update data object with only non-empty fields
      const updateData = {
        ids: selectedAccounts,
      };

      if (formData.accountType) {
        updateData.frontend_account_type = formData.accountType;
      }

      if (formData.manager) {
        updateData.manager = formData.manager;
      }

      if (formData.bookkeeper) {
        updateData.bookkeeper = formData.bookkeeper;
      }

      // Call the bulk update API
      const response = await updateCustomerBulk(updateData);

      if (onSubmit) {
        onSubmit();
      }

      if (onShowSuccess) {
        const updatedCount = response.data.updated || selectedAccounts.length;
        onShowSuccess(`${updatedCount} account(s) updated successfully!`);
      }

      onClose();
    } catch (error) {
      console.error("Bulk update error:", error);

      // Show error message to user
      if (onShowSuccess) {
        const errorMessage = error.response?.data?.error || "Failed to update accounts. Please try again.";
        onShowSuccess(errorMessage, "error");
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, selectedAccounts, onSubmit, onShowSuccess, onClose]);

  // Check if at least one field is filled
  const isFormValid = () => {
    return formData.accountType || formData.manager || formData.bookkeeper;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
      sx={{
        zIndex: 99999,
        '& .MuiAutocomplete-popper': {
          zIndex: 999999,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Bulk Edit Accounts ({selectedAccounts.length})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Here, you can view account details, track connections, and edit essentials.
            </Typography>
          </Box>
          <CloseIcon
            onClick={onClose}
            sx={{
              cursor: 'pointer',
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' }
            }}
          />
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Primary Fields
            </Typography>

            <Grid container rowSpacing={3} columnSpacing={2}>
              <Grid size={6}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  size="small"
                >
                  <InputLabel id="account-type-label">Select Account Type</InputLabel>
                  <Select
                    labelId="account-type-label"
                    id="account-type-select"
                    value={formData.accountType}
                    onChange={handleChange("accountType")}
                    label="Select Account Type"
                    MenuProps={{
                      disablePortal: true,
                      PaperProps: {
                        style: {
                          zIndex: 99999,
                        },
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Select Account Type</em>
                    </MenuItem>
                    <MenuItem value="Trial Demo">Trial Demo</MenuItem>
                    <MenuItem value="Unpublished">Unpublished</MenuItem>
                    <MenuItem value="Published">Published</MenuItem>
                    <MenuItem value="Platform">Platform</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={6}>
                <Autocomplete
                  options={managers}
                  value={selectedManager}
                  loading={loadingManagers}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.name || option.email || '';
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return option.id === value.id;
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === 'input') {
                      setFormData(prev => ({ ...prev, manager: newInputValue || "" }));
                      setSelectedManager(null);
                      searchManagers(newInputValue || "");
                    } else if (reason === 'clear') {
                      setFormData(prev => ({ ...prev, manager: "" }));
                      setSelectedManager(null);
                      searchManagers("");
                    }
                  }}
                  onChange={(event, newValue, reason) => {
                    if (newValue && typeof newValue === 'object') {
                      setSelectedManager(newValue);
                      setFormData(prev => ({
                        ...prev,
                        manager: newValue.name
                      }));
                    } else if (newValue === null || newValue === '') {
                      setSelectedManager(null);
                      setFormData(prev => ({ ...prev, manager: "" }));
                    }
                  }}
                  filterOptions={(options) => options}
                  open={managerOpen}
                  onOpen={() => {
                    setManagerOpen(true);
                    if (managers.length === 0 && !loadingManagers) {
                      searchManagers(formData.manager || "");
                    }
                  }}
                  onClose={() => {
                    setManagerOpen(false);
                  }}
                  selectOnFocus
                  clearOnBlur={false}
                  handleHomeEndKeys
                  loadingText="Loading managers..."
                  noOptionsText="Type to search managers..."
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assign Manager"
                      size="small"
                      fullWidth
                      variant="outlined"
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    const uniqueKey = `manager-${option.id}`;
                    return (
                      <li key={uniqueKey} {...otherProps}>
                        <div>
                          <div style={{ fontWeight: 'bold' }}>
                            {option.firstName || option.lastName
                              ? `${option.firstName} ${option.lastName}`.trim()
                              : option.email
                            }
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#666' }}>
                            {option.email} • {option.type}
                          </div>
                        </div>
                      </li>
                    );
                  }}
                  slotProps={{
                    popper: {
                      placement: 'bottom-start',
                      style: {
                        zIndex: 999999,
                        position: 'fixed'
                      },
                      modifiers: [
                        {
                          name: 'flip',
                          enabled: false,
                        },
                        {
                          name: 'preventOverflow',
                          enabled: false,
                        },
                      ],
                    },
                  }}
                  ListboxProps={{
                    style: {
                      maxHeight: '200px',
                      overflow: 'auto'
                    }
                  }}
                />
              </Grid>

              <Grid size={6}>
                <Autocomplete
                  options={bookkeepers}
                  value={selectedBookkeeper}
                  loading={loadingBookkeepers}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.name || option.email || '';
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return option.id === value.id;
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === 'input') {
                      setFormData(prev => ({ ...prev, bookkeeper: newInputValue || "" }));
                      setSelectedBookkeeper(null);
                      searchBookkeepers(newInputValue || "");
                    } else if (reason === 'clear') {
                      setFormData(prev => ({ ...prev, bookkeeper: "" }));
                      setSelectedBookkeeper(null);
                      searchBookkeepers("");
                    }
                  }}
                  onChange={(event, newValue, reason) => {
                    if (newValue && typeof newValue === 'object') {
                      setSelectedBookkeeper(newValue);
                      setFormData(prev => ({
                        ...prev,
                        bookkeeper: newValue.name
                      }));
                    } else if (newValue === null || newValue === '') {
                      setSelectedBookkeeper(null);
                      setFormData(prev => ({ ...prev, bookkeeper: "" }));
                    }
                  }}
                  filterOptions={(options) => options}
                  open={bookkeeperOpen}
                  onOpen={() => {
                    setBookkeeperOpen(true);
                    if (bookkeepers.length === 0 && !loadingBookkeepers) {
                      searchBookkeepers(formData.bookkeeper || "");
                    }
                  }}
                  onClose={() => {
                    setBookkeeperOpen(false);
                  }}
                  selectOnFocus
                  clearOnBlur={false}
                  handleHomeEndKeys
                  loadingText="Loading accountants..."
                  noOptionsText="Type to search accountants..."
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assign Bookkeeper"
                      size="small"
                      fullWidth
                      variant="outlined"
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    const uniqueKey = `bookkeeper-${option.id}`;
                    return (
                      <li key={uniqueKey} {...otherProps}>
                        <div>
                          <div style={{ fontWeight: 'bold' }}>
                            {option.firstName || option.lastName
                              ? `${option.firstName} ${option.lastName}`.trim()
                              : option.email
                            }
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#666' }}>
                            {option.email} • {option.type}
                          </div>
                        </div>
                      </li>
                    );
                  }}
                  slotProps={{
                    popper: {
                      placement: 'bottom-start',
                      style: {
                        zIndex: 999999,
                        position: 'fixed'
                      },
                      modifiers: [
                        {
                          name: 'flip',
                          enabled: false,
                        },
                        {
                          name: 'preventOverflow',
                          enabled: false,
                        },
                      ],
                    },
                  }}
                  ListboxProps={{
                    style: {
                      maxHeight: '200px',
                      overflow: 'auto'
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1, position: 'sticky', bottom: 0, backgroundColor: 'white', zIndex: 10 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: "#627CFB",
            borderColor: "#627CFB",
            textTransform: "none",
            "&:hover": {
              borderColor: "#536DEC",
              backgroundColor: "rgba(98, 124, 251, 0.04)",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid() || isSubmitting}
          sx={{
            backgroundColor: "#627CFB",
            textTransform: "none",
            "&:hover": { backgroundColor: "#536DEC" },
          }}
        >
          {isSubmitting ? "Updating..." : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

